import { FlexBox, SectionLoader } from '@airship-platform/jam-web/components';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import React, { Fragment } from 'react';
import { Category } from '../../../../data-access/products/products.models';

export interface CategoriesListProps {
  categories: Category[];
  isLoading: boolean;
  error?: string;
  onCategoryClick: (category: Category) => void;
  renderCategoryItem?: (
    category: Category,
    onCategoryClick: CategoriesListProps['onCategoryClick']
  ) => React.ReactNode;
}

const CategoriesList: React.FC<CategoriesListProps> = ({
  categories,
  error,
  isLoading,
  onCategoryClick,
  renderCategoryItem,
}) => {
  if (isLoading) {
    return <SectionLoader isLoading title={'Fetching categories...'} />;
  }
  if (error && !isLoading) {
    return (
      <Alert
        severity="error"
        action={
          <Button
            variant="text"
            color="blue"
            size="small"
            onClick={(e) => {
              e.preventDefault();
              window.location.reload();
            }}
          >
            Click here to refresh page
          </Button>
        }
        sx={{
          flexDirection: ['column', 'row'],
        }}
      >
        We couldn't load the categories ({error}).
      </Alert>
    );
  }

  if (!categories.length) {
    return null;
  }

  const renderCategoryItemDefault = (category: Category) =>
    renderCategoryItem ? (
      <Fragment key={category.categoryId}>
        {renderCategoryItem(category, onCategoryClick)}
      </Fragment>
    ) : (
      <FlexBox
        key={category.categoryId}
        flexDirection={'column'}
        alignItems={'center'}
        cursor={'pointer'}
        userSelect={'none'}
        borderRadius={2}
        border={(theme) => `1px solid ${theme.palette.grey[300]}`}
        onClick={() => onCategoryClick(category)}
      >
        <FlexBox
          backgroundImage={(theme) =>
            category.image
              ? `url(${category.image})`
              : `linear-gradient(${theme.palette.grey[100]}, ${theme.palette.grey[100]})`
          }
          backgroundSize={'contain'}
          backgroundPosition={'center'}
          backgroundRepeat={'no-repeat'}
          width={'100%'}
          height={'7.5rem'}
          borderRadius={'8px 8px 0 0'}
        />
        <Typography variant={'subtitle2'} sx={{ p: 2, textAlign: 'center' }}>
          {category.name}
        </Typography>
      </FlexBox>
    );

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: 2,
      }}
    >
      {categories.map((category) => renderCategoryItemDefault(category))}
    </Box>
  );
};

export default CategoriesList;
